import { defaultRetry } from "@/queries/errors";
import { isCreditsExhaustionError, isVerifyCardError } from "@/utils/errors";
import { ConnectError } from "@connectrpc/connect";
import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider as ReactQueryClientProvider,
    type Mutation,
} from "@tanstack/react-query";
import {
    OrganizationTierFailedPreconditionDetails,
    type OrganizationTier,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { createContext, useMemo, useState, useCallback, useRef, type PropsWithChildren } from "react";

// Middleware types
export type QueryErrorMiddleware = (error: Error) => void;
export type MutationErrorMiddleware = (
    error: Error,
    variables: unknown,
    context: unknown,
    mutation: Mutation<unknown, unknown, unknown, unknown>,
) => void;

export type QueryClientMiddleware = {
    onQueryError?: QueryErrorMiddleware;
    onMutationError?: MutationErrorMiddleware;
};

// Middleware context
export const QueryClientMiddlewareContext = createContext<{
    registerMiddleware: (id: string, middleware: QueryClientMiddleware) => void;
    unregisterMiddleware: (id: string) => void;
}>({
    registerMiddleware: () => {
        // Default implementation - will be overridden by provider
    },
    unregisterMiddleware: () => {
        // Default implementation - will be overridden by provider
    },
});

export const TierViolationContext = createContext<{
    isTierViolated: boolean;
    message: string | undefined;
    requiredTier: OrganizationTier | undefined;
    onCloseCallback: (() => void) | undefined;
    /**
     * Clears the onCloseCallback.
     */
    clearCallback: () => void;
    setTierViolation: (
        open: boolean,
        error?: OrganizationTierFailedPreconditionDetails,
        onCloseCallback?: () => void,
    ) => void;
}>({
    isTierViolated: false,
    message: undefined,
    requiredTier: undefined,
    onCloseCallback: undefined,
    clearCallback: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
    setTierViolation: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
});

export const CreditsExhaustionContext = createContext<{
    isCreditsExhausted: boolean;
    message: string | undefined;
    onCloseCallback: (() => void) | undefined;
    /**
     * Clears the onCloseCallback.
     */
    clearCallback: () => void;
    setCreditsExhaustion: (open: boolean, message?: string, onCloseCallback?: () => void) => void;
}>({
    isCreditsExhausted: false,
    message: undefined,
    onCloseCallback: undefined,
    clearCallback: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
    setCreditsExhaustion: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
});

export const VerifyCardContext = createContext<{
    isVerificationRequired: boolean;
    message: string | undefined;
    onCloseCallback: (() => void) | undefined;
    /**
     * Clears the onCloseCallback.
     */
    clearCallback: () => void;
    setVerificationRequired: (open: boolean, message?: string, onCloseCallback?: () => void) => void;
}>({
    isVerificationRequired: false,
    message: undefined,
    onCloseCallback: undefined,
    clearCallback: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
    setVerificationRequired: () => {
        // just an empty setter by default, but ESLint wants some content anyway
    },
});


export const QueryClientProvider = ({ children }: PropsWithChildren) => {
    const [isTierViolated, setIsTierViolated] = useState(false);
    const [message, setMessage] = useState<string | undefined>(undefined);
    const [requiredTier, setRequiredTier] = useState<OrganizationTier | undefined>(undefined);
    // the double arrow function makes sure that the useState does not consider it a getter for the initial value
    const [tierViolationOnCloseCallback, setTierViolationOnCloseCallback] = useState<(() => () => void) | undefined>(
        undefined,
    );
    const handleTierViolation = useCallback(
        (open: boolean, error?: OrganizationTierFailedPreconditionDetails, onCloseCallback?: () => void) => {
            setIsTierViolated(open);
            setRequiredTier(error?.requiredTier);
            setMessage(error?.message);
            if (onCloseCallback) {
                setTierViolationOnCloseCallback(() => onCloseCallback);
            }
        },
        [],
    );

    // Credits exhaustion state
    const [isCreditsExhausted, setIsCreditsExhausted] = useState(false);
    const [creditsMessage, setCreditsMessage] = useState<string | undefined>(undefined);
    const [creditsOnCloseCallback, setCreditsOnCloseCallback] = useState<(() => () => void) | undefined>(undefined);
    const handleCreditsExhaustion = useCallback((open: boolean, message?: string, onCloseCallback?: () => void) => {
        setIsCreditsExhausted(open);
        setCreditsMessage(message);
        if (onCloseCallback) {
            setCreditsOnCloseCallback(() => onCloseCallback);
        }
    }, []);

    // Verify card state
    const [isVerificationRequired, setIsVerificationRequired] = useState(false);
    const [verificationMessage, setVerificationMessage] = useState<string | undefined>(undefined);
    const [verificationOnCloseCallback, setVerificationOnCloseCallback] = useState<(() => () => void) | undefined>(undefined);
    const handleVerificationRequired = useCallback((open: boolean, message?: string, onCloseCallback?: () => void) => {
        setIsVerificationRequired(open);
        setVerificationMessage(message);
        if (onCloseCallback) {
            setVerificationOnCloseCallback(() => onCloseCallback);
        }
    }, []);

    // Middleware management using useRef for stable reference
    const middlewaresRef = useRef<Map<string, QueryClientMiddleware>>(new Map());

    const registerMiddleware = useCallback((id: string, middleware: QueryClientMiddleware) => {
        middlewaresRef.current.set(id, middleware);
    }, []);

    const unregisterMiddleware = useCallback((id: string) => {
        middlewaresRef.current.delete(id);
    }, []);

    const clearTierViolationCallback = useCallback(() => {
        setTierViolationOnCloseCallback(undefined);
    }, []);

    const clearCreditsCallback = useCallback(() => {
        setCreditsOnCloseCallback(undefined);
    }, []);

    const clearVerificationCallback = useCallback(() => {
        setVerificationOnCloseCallback(undefined);
    }, []);

    const queryClient = useMemo(() => {
        return new QueryClient({
            queryCache: new QueryCache({
                onError: (error) => {
                    // Handle tier violations
                    if (error instanceof ConnectError) {
                        const violations = error.findDetails(OrganizationTierFailedPreconditionDetails);
                        if (violations.length > 0) {
                            handleTierViolation(true, violations[0]);
                        }

                        // Handle credits exhaustion
                        if (isCreditsExhaustionError(error)) {
                            handleCreditsExhaustion(true, error.message);
                        }

                        // Handle verify card
                        if (isVerifyCardError(error)) {
                            handleVerificationRequired(true, error.message);
                        }
                    }

                    const middlewares = middlewaresRef.current;
                    middlewares.forEach((middleware) => {
                        middleware.onQueryError?.(error);
                    });
                },
            }),
            mutationCache: new MutationCache({
                onError: (error, vars, ctx, mutation) => {
                    // Handle tier violations
                    if (error instanceof ConnectError) {
                        const orgTierViolation = error.findDetails(OrganizationTierFailedPreconditionDetails);
                        if (orgTierViolation.length > 0) {
                            handleTierViolation(true, orgTierViolation[0]);
                        }

                        // Handle credits exhaustion
                        if (isCreditsExhaustionError(error)) {
                            handleCreditsExhaustion(true, error.message);
                        }

                        // Handle verify card
                        if (isVerifyCardError(error)) {
                            handleVerificationRequired(true, error.message);
                        }
                    }

                    const middlewares = middlewaresRef.current;
                    middlewares.forEach((middleware) => {
                        middleware.onMutationError?.(error, vars, ctx, mutation);
                    });
                },
            }),
            defaultOptions: {
                queries: {
                    refetchOnReconnect: true,
                    staleTime: 1_000 * 5, // 5 seconds
                    retry: defaultRetry,
                },
            },
        });
    }, [handleTierViolation, handleCreditsExhaustion, handleVerificationRequired]);

    return (
        <ReactQueryClientProvider client={queryClient}>
            <QueryClientMiddlewareContext.Provider
                value={{
                    registerMiddleware,
                    unregisterMiddleware,
                }}
            >
                <TierViolationContext.Provider
                    value={{
                        isTierViolated,
                        message,
                        requiredTier,
                        setTierViolation: handleTierViolation,
                        onCloseCallback: tierViolationOnCloseCallback,
                        clearCallback: clearTierViolationCallback,
                    }}
                >
                    <CreditsExhaustionContext.Provider
                        value={{
                            isCreditsExhausted,
                            message: creditsMessage,
                            setCreditsExhaustion: handleCreditsExhaustion,
                            onCloseCallback: creditsOnCloseCallback,
                            clearCallback: clearCreditsCallback,
                        }}
                    >
                        <VerifyCardContext.Provider
                            value={{
                                isVerificationRequired,
                                message: verificationMessage,
                                setVerificationRequired: handleVerificationRequired,
                                onCloseCallback: verificationOnCloseCallback,
                                clearCallback: clearVerificationCallback,
                            }}
                        >
                            {children}
                        </VerifyCardContext.Provider>
                    </CreditsExhaustionContext.Provider>
                </TierViolationContext.Provider>
            </QueryClientMiddlewareContext.Provider>
        </ReactQueryClientProvider>
    );
};
